import { useState, useEffect } from 'react';
import { Grid, Button, Typography, TextField, Badge, Menu, darken, useTheme } from '@mui/material';
import { MenuItem, ListItemIcon, Tooltip, IconButton, ListItemButton } from '@mui/material';
import { grey } from '@mui/material/colors';
import { FileCloudIcon, ActionIcon, TaskIcon } from '../../Icons';
import { ArrowRightIcon, QuestionMarkIcon } from '../../Icons';
import StateManager from '../../StateManager';
import { FileDialog } from '../FieldCell';
import ActionsDialog from '../../../Hubs/audits/components/ActionDialog';
import NewTask from '../../../Hubs/tasks/newTask';
import { Action } from '../../../Global/Fields/ActionGroup';
import axios from 'axios';
import { isArray, isEmpty } from 'lodash';
import { DebouncedTextField } from '../../Components';

export default function CheckField({ item, onResult, editable, activityInfo, formEntryId }) {
  const [result, setResult] = useState({ comments: '' });
  const [fileDialogOpen, setFileDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [initialAction, setInitialAction] = useState({});
  const [internalActions, setInternalActions] = useState([]);
  const [externalActions, setExternalActions] = useState([]);
  const [newTaskDialogOpen, setNewTaskDialogOpen] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const actionReference = `field&${activityInfo?.entryId}&${item.id}`;
  const theme = useTheme();

  useEffect(() => {
    setExternalActions(isArray(item?.filledValue?.actions) ? item?.filledValue?.actions : []);

    setResult({
      comments: item?.filledValue?.comments || '',
      ...item?.filledValue,
    });
  }, [item?.filledValue]);

  useEffect(() => {
    if (!item?.id || !formEntryId) return;

    axios
      .get('/tasks/data/getCheckFieldTaskData', { params: { fieldId: item?.id, entryId: formEntryId } })
      .then((data) => setInternalActions(isArray(data.data) ? data.data : []));
  }, [item?.id, formEntryId]);

  const checkedItem = result?.value || null;
  const files = result?.files || [];
  const fieldSettings = item?.checkFieldOptions;

  function handleCommentChange(value) {
    const updatedResult = { ...result, comments: value };
    setResult(updatedResult);
    setSubmitAttempted(true);
    if (fieldSettings?.isCommentRequired && !value) {
      StateManager.setAxiosErrorAlert('Comment is required');
      return;
    }
    onResult(updatedResult);
  }

  function saveOption(optionId, weight) {
    const updatedResult = { ...result, value: optionId, weight };
    setResult(updatedResult);
    onResult(updatedResult);
  }

  function saveFiles(files) {
    const updatedResult = { ...result, files };
    setResult(updatedResult);
    onResult(updatedResult);
  }

  function addAction(action) {
    const index = externalActions.findIndex((item) => item._id === action._id);

    let newActions;
    if (index !== -1) {
      newActions = externalActions.map((item, i) => (i === index ? action : item));
    } else {
      newActions = [...externalActions, action];
    }
    setExternalActions(newActions);
    const updatedResult = { ...result, actions: newActions };
    setResult(updatedResult);
    onResult(updatedResult);
  }

  function deleteAction(actionId) {
    const remainingInternalActions = internalActions.filter((action) => action._id !== actionId);
    const remainingExternalActions = externalActions.filter((action) => action._id !== actionId);
    setInternalActions(remainingInternalActions);
    setExternalActions(remainingExternalActions);
    const updatedResult = { ...result, actions: remainingExternalActions };
    setResult(updatedResult);
    onResult(updatedResult);
  }

  return (
    <Grid container spacing={1} justifyContent={'space-between'} alignItems={'flex-start'}>
      <Grid container item justifyContent={'flex-start'} gap={2} mt={1}>
        {item?.options.map((option) => {
          const optionSelected = checkedItem === option?.id;
          const optionColor = option.color ? option.color : theme.palette.primary.main;
          const optionHoverColor = option.color ? darken(option.color, 0.15) : theme.palette.primary.dark;

          return (
            <Button
              key={option?.id}
              disabled={!editable}
              variant={'outlined'}
              onClick={() => saveOption(option?.id, option?.weight)}
              sx={{
                borderRadius: 3,
                borderWidth: 2,
                borderColor: optionColor,
                textTransform: 'none',
                color: optionSelected ? (theme) => theme.palette.getContrastText(optionColor) : optionColor,
                background: optionSelected ? optionColor : '',
                '&:hover': {
                  background: optionSelected ? optionHoverColor : '',
                  borderWidth: 2,
                  borderColor: optionHoverColor,
                },
                '&:disabled': {
                  color: optionSelected ? (theme) => theme.palette.getContrastText(optionColor) : optionColor,
                  background: optionSelected ? optionColor : '',
                },
              }}
            >
              {option?.text}
            </Button>
          );
        })}
        {fieldSettings?.isNotApplicableOption && (
          <Button
            key={'n/a'}
            variant={'outlined'}
            onClick={() => saveOption('n/a')}
            disabled={!editable}
            sx={{
              borderRadius: 3,
              borderWidth: 2,
              borderColor: grey[500],
              textTransform: 'none',
              color: 'black',
              background: checkedItem === 'n/a' ? grey[500] : '',
              '&:hover': {
                background: checkedItem === 'n/a' ? grey[600] : '',
                borderWidth: 2,
                borderColor: grey[600],
              },
              '&:disabled': {
                color: 'black',
              },
            }}
          >
            {'N/A'}
          </Button>
        )}
        <Grid container item>
          {editable && (
            <DebouncedTextField
              fullWidth
              error={fieldSettings?.isCommentRequired && submitAttempted && !result?.comments}
              helperText={fieldSettings?.isCommentRequired && !result?.comments ? 'Comment is required' : ''}
              label="Add a comment"
              name="comment"
              value={result?.comments || ''}
              onValueChange={handleCommentChange}
              variant="outlined"
              required={fieldSettings?.isCommentRequired}
              id="check-field-comment"
            />
          )}
          {!editable && (
            <Grid container item direction={'row'}>
              <Typography>Comment: {result?.comments || 'No comment provided'}</Typography>
            </Grid>
          )}
        </Grid>
        <Grid container item sx={{ my: 1 }}>
          {(editable || !isEmpty(files)) && (
            <Button
              variant="outlined"
              onClick={() => setFileDialogOpen(true)}
              endIcon={
                <Badge badgeContent={files.length} invisible={isEmpty(files)} color="primary">
                  <FileCloudIcon style={{ height: '65%', margin: 'auto' }} />
                </Badge>
              }
            >
              {`${editable ? 'Add a' : 'A'}ttachments`}
            </Button>
          )}
          {editable && (
            <Button
              variant="outlined"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              endIcon={
                <Badge badgeContent={internalActions.length + externalActions.length} color="primary">
                  <ActionIcon style={{ height: '65%', margin: 'auto' }} />
                </Badge>
              }
              sx={{ ml: 2 }}
            >
              Create an action
            </Button>
          )}
        </Grid>
      </Grid>
      <Menu anchorEl={anchorEl} open={editable && Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem
          onClick={() => {
            setNewTaskDialogOpen(true);
          }}
        >
          <ListItemIcon>
            <TaskIcon />
          </ListItemIcon>
          Raise an action internally
          <div style={{ display: 'inline-flex', alignItems: 'center' }}>
            <Tooltip
              placement="top"
              enterDelay={100}
              title="Creates an internal task for sending to registered users."
              PopperProps={{ style: { zIndex: 1500 } }}
            >
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <QuestionMarkIcon style={{ marginLeft: 5, pointerEvents: 'auto' }} />
              </span>
            </Tooltip>
          </div>
        </MenuItem>
        <MenuItem onClick={() => setActionDialogOpen(true)}>
          <ListItemIcon>
            <ArrowRightIcon />
          </ListItemIcon>
          Raise an external action
          <div style={{ display: 'inline-flex', alignItems: 'center' }}>
            <Tooltip
              placement="top"
              enterDelay={100}
              title="Creates a task for an external user (not sent)."
              PopperProps={{ style: { zIndex: 1500 } }}
            >
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <QuestionMarkIcon style={{ marginLeft: 5, pointerEvents: 'auto' }} />
              </span>
            </Tooltip>
          </div>
        </MenuItem>
      </Menu>
      <Grid container item spacing={1} m={0}>
        {internalActions.map((action, index) => (
          <Action
            key={index}
            initial={action}
            onDelete={deleteAction}
            editable={editable}
            onClick={() => {
              setInitialAction(action);
              setActionDialogOpen(true);
            }}
          />
        ))}
        {externalActions.map((action, index) => (
          <Action
            key={index}
            initial={action}
            onDelete={deleteAction}
            editable={editable}
            external
            onClick={() => {
              setInitialAction(action);
              setActionDialogOpen(true);
            }}
          />
        ))}
      </Grid>
      <FileDialog
        open={fileDialogOpen}
        editable={editable}
        initial={files}
        onClose={() => setFileDialogOpen(false)}
        onResult={saveFiles}
      />
      <ActionsDialog
        open={actionDialogOpen}
        editable={editable}
        onClose={() => {
          setActionDialogOpen(false);
          setInitialAction({});
        }}
        forCheckField={true}
        onStatusChange={(status) =>
          setInitialAction((prev) => {
            return { ...prev, progress: status };
          })
        }
        onResult={addAction}
        initial={initialAction}
      />
      <NewTask
        open={newTaskDialogOpen}
        onClose={() => setNewTaskDialogOpen(false)}
        actionReference={actionReference}
        actionReferenceInfo={{ ...activityInfo, fieldId: item.id }}
        onResult={(res) => setInternalActions([...internalActions, res])}
      />
    </Grid>
  );
}
